import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, BarChart2, Cpu, Eye, EyeOff, Loader2, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, inr, num, pct, tone } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Equity, Metric } from "@/components/BacktestLab";

const RISK_CLS = { Low: "text-[#00E676] border-[#00E676]/40", Medium: "text-amber-300 border-amber-400/40", High: "text-[#FF3B30] border-[#FF3B30]/40" };

export const LiveDeployDialog = ({ strategy, open, onOpenChange, onDeployed }) => {
  const [f, setF] = useState({ max_capital: 50000, daily_loss_limit: 2000, confirm: false });
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.post("/paper/bots", { strategy_id: strategy.id, mode: "LIVE", confirm: f.confirm, max_capital: Number(f.max_capital), daily_loss_limit: Number(f.daily_loss_limit) || null });
      toast.success(`LIVE bot deployed: ${strategy.name}`); onOpenChange(false); onDeployed?.();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (!strategy) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="live-deploy-dialog" className="bg-[#0B1F3A] border-[#FF3B30]/40 text-slate-200 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-white flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#FF3B30]" /> Live execution · {strategy.name}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">This bot will place REAL market orders in your AliceBlue account ({strategy.symbol} · {strategy.timeframe} · {strategy.product}).</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1"><span className="widget-title">Max capital (₹)</span>
            <input data-testid="live-max-capital-input" type="number" className="field" value={f.max_capital} onChange={(e) => setF({ ...f, max_capital: e.target.value })} /></label>
          <label className="flex flex-col gap-1"><span className="widget-title">Daily loss limit (₹)</span>
            <input data-testid="live-loss-limit-input" type="number" className="field" value={f.daily_loss_limit} onChange={(e) => setF({ ...f, daily_loss_limit: e.target.value })} /></label>
        </div>
        <label className="flex items-start gap-2 text-xs text-slate-300 cursor-pointer p-2 border border-[#163056] rounded-sm">
          <input data-testid="live-confirm-checkbox" type="checkbox" checked={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.checked })} className="mt-0.5 accent-[#FF3B30]" />
          I understand this places real orders with real money. Trading losses are my responsibility. New entries stop for the day once the loss limit is hit; the kill switch squares off everything.
        </label>
        <button data-testid="live-deploy-confirm-button" disabled={busy || !f.confirm} onClick={go} className="btn-bear w-full h-9">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Deploy LIVE bot
        </button>
      </DialogContent>
    </Dialog>
  );
};

const Card = ({ s, selected, onSelect, admin, onPublish }) => (
  <button onClick={() => onSelect(s)} data-testid={`catalog-card-${s.id}`}
    className={`panel p-4 text-left transition-[border-color,transform] duration-150 hover:-translate-y-0.5 ${selected ? "border-[#22A34A]" : "hover:border-[#334155]"} ${admin && !s.published ? "opacity-60" : ""}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="font-display font-bold text-white leading-tight">{s.name}</div>
      <span className={`shrink-0 text-[10px] font-mono px-1.5 py-0.5 border rounded-sm ${RISK_CLS[s.risk_level] || RISK_CLS.Medium}`}>{(s.risk_level || "Medium").toUpperCase()} RISK</span>
    </div>
    <div className="font-mono text-[10px] text-slate-500 mt-1">{s.symbol} · {s.timeframe} · {s.product} · SL {s.stop_loss_pct}% · TP {s.take_profit_pct || "—"}%</div>
    <p className="text-xs text-slate-400 mt-2 leading-relaxed min-h-[48px]">{s.description || "No description yet."}</p>
    <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-[#102A4A]">
      <div><div className="widget-title">Win rate</div><div className="font-mono text-sm text-slate-100" data-testid={`catalog-winrate-${s.id}`}>{s.win_rate == null ? "—" : `${num(s.win_rate, 1)}%`}</div></div>
      <div><div className="widget-title">Return</div><div className={`font-mono text-sm ${tone(s.stats?.total_return_pct)}`}>{s.stats ? pct(s.stats.total_return_pct) : "—"}</div></div>
      <div><div className="widget-title">Max DD</div><div className="font-mono text-sm text-[#FF3B30]">{s.stats ? `${num(s.stats.max_drawdown_pct, 1)}%` : "—"}</div></div>
    </div>
    {admin && (
      <div className="mt-3 flex items-center justify-between">
        <span className={`text-[10px] font-mono ${s.published ? "text-[#00E676]" : "text-slate-500"}`}>{s.published ? "PUBLISHED" : "DRAFT"}</span>
        <span role="button" data-testid={`catalog-publish-${s.id}`} onClick={(e) => { e.stopPropagation(); onPublish(s); }}
          className="btn-ghost h-7 text-xs">{s.published ? <><EyeOff className="w-3 h-3" /> Unpublish</> : <><Eye className="w-3 h-3" /> Publish</>}</span>
      </div>
    )}
  </button>
);

export default function Catalog({ goPaper }) {
  const { isAdmin } = useAuth();
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [liveOpen, setLiveOpen] = useState(false);
  const [broker, setBroker] = useState(null);

  const load = () => api.get(isAdmin ? "/strategies" : "/catalog").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); api.get("/broker/status").then((r) => setBroker(r.data)).catch(() => {}); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const publish = async (s) => {
    try { await api.post(`/strategies/${s.id}/publish`, { published: !s.published }); toast.success(s.published ? "Removed from catalog" : "Published to catalog"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const backtest = async () => {
    setBusy(true); setRes(null);
    try { const { data } = await api.post(`/catalog/${sel.id}/backtest`); setRes(data); toast.success(`Backtest complete: ${data.metrics.total_trades} trades`); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const paper = async () => {
    try { await api.post("/paper/bots", { strategy_id: sel.id, mode: "PAPER" }); toast.success(`Paper bot deployed: ${sel.name}`); goPaper(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const m = res?.metrics;
  const published = list.filter((s) => s.published || !isAdmin);
  return (
    <div className="space-y-3 rise" data-testid="catalog">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-xl font-bold text-white tracking-tight">Strategy Catalog</h1>
        <span className="font-mono text-[10px] px-2 py-0.5 border border-[#163056] text-slate-400 rounded-sm">{published.length}/5 PROPRIETARY STRATEGIES</span>
        {isAdmin && <span className="text-xs text-slate-500">Clients only see published strategies. Edit rules in the Strategy Builder.</span>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="catalog-grid">
        {list.map((s) => <Card key={s.id} s={s} selected={sel?.id === s.id} onSelect={(x) => { setSel(x); setRes(null); }} admin={isAdmin} onPublish={publish} />)}
        {!list.length && <div className="panel p-10 text-center text-sm text-slate-500 col-span-full">No strategies published yet. Check back soon.</div>}
      </div>

      {sel && (isAdmin ? sel.published : true) && (
        <div className="panel p-4 space-y-3" data-testid="catalog-deploy-panel">
          <div className="flex flex-wrap items-center gap-3">
            <div><div className="widget-title">Selected</div><div className="font-display font-bold text-white">{sel.name}</div></div>
            <div className="ml-auto flex flex-wrap gap-2">
              <button data-testid="catalog-backtest-button" onClick={backtest} disabled={busy} className="btn-ghost h-9">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BarChart2 className="w-3.5 h-3.5" />} Backtest
              </button>
              <button data-testid="catalog-paper-button" onClick={paper} className="btn-bull h-9"><Cpu className="w-3.5 h-3.5" /> Paper Trade</button>
              <button data-testid="catalog-live-button" onClick={() => setLiveOpen(true)} className="btn-bear h-9"><Zap className="w-3.5 h-3.5" /> Live Execution</button>
            </div>
          </div>
          {!broker?.my_session?.connected && <p className="text-[11px] text-amber-300">Live execution needs your AliceBlue account connected — open Connections (gear icon) first.</p>}
          {res && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="catalog-backtest-metrics">
                <Metric label="Net Return" value={inr(m.total_return, 0)} cls={tone(m.total_return)} />
                <Metric label="Return %" value={pct(m.total_return_pct)} cls={tone(m.total_return_pct)} />
                <Metric label="Buy & Hold" value={pct(m.buy_hold_return_pct)} cls={tone(m.buy_hold_return_pct)} />
                <Metric label="Win Rate" value={`${num(m.win_rate_pct)}%`} />
                <Metric label="Profit Factor" value={num(m.profit_factor)} cls={m.profit_factor >= 1 ? "text-bull" : "text-bear"} />
                <Metric label="Max Drawdown" value={`${num(m.max_drawdown_pct)}%`} cls="text-bear" />
                <Metric label="Sharpe" value={num(m.sharpe)} />
                <Metric label="Trades W/L" value={`${m.total_trades} · ${m.wins}/${m.losses}`} />
              </div>
              <div className="panel"><div className="px-3 py-2 border-b border-[#163056] widget-title">Equity Curve vs Buy & Hold</div><Equity data={res.equity_curve} /></div>
            </>
          )}
        </div>
      )}
      <LiveDeployDialog strategy={sel} open={liveOpen} onOpenChange={setLiveOpen} onDeployed={goPaper} />
    </div>
  );
}
